import React, { createContext, useContext, useEffect, useState } from "react";
import { pusherClient } from "@/lib/pusher/pusher-client";
import { PusherConnectionStatus } from "@/lib/pusher/types";
import { ActionCableConnectionStatus } from "@/lib/actioncable/types";
import { env } from "@/env";

interface RealtimeContextValue {
  connectionStatus: PusherConnectionStatus;
  isPusherEnabled: boolean;
  isActionCableEnabled: boolean;
  actionCableUrl: string | null;
  actionCableConnectionStatus: ActionCableConnectionStatus;
  setActionCableConnectionStatus: (status: ActionCableConnectionStatus) => void;
}

const RealtimeContext = createContext<RealtimeContextValue>({
  connectionStatus: {
    state: "disconnected",
    isConnected: false,
  },
  isPusherEnabled: false,
  isActionCableEnabled: false,
  actionCableUrl: null,
  actionCableConnectionStatus: {
    state: "disconnected",
    isConnected: false,
  },
  setActionCableConnectionStatus: () => {},
});

export const useRealtime = () => useContext(RealtimeContext);

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const [connectionStatus, setConnectionStatus] =
    useState<PusherConnectionStatus>({
      state: "disconnected",
      isConnected: false,
    });

  const [actionCableConnectionStatus, setActionCableConnectionStatus] =
    useState<ActionCableConnectionStatus>({
      state: "disconnected",
      isConnected: false,
    });

  const isPusherEnabled =
    typeof window !== "undefined" && !!env.NEXT_PUBLIC_PUSHER_KEY;

  const isActionCableEnabled = typeof window !== "undefined";
  const actionCableUrl = env.NEXT_PUBLIC_ACTIONCABLE_URL ?? null;

  useEffect(() => {
    const handleStateChange = ({
      current,
    }: {
      previous: string;
      current: string;
    }) => {
      const isConnected = current === "connected";
      setConnectionStatus((prev) => {
        if (prev.state !== current || prev.isConnected !== isConnected) {
          return {
            state: current as PusherConnectionStatus["state"],
            isConnected,
          };
        }
        return prev;
      });
    };

    if (isPusherEnabled) {
      Promise.resolve().then(() => {
        try {
          pusherClient.initialize({
            key: env.NEXT_PUBLIC_PUSHER_KEY!,
            cluster: env.NEXT_PUBLIC_PUSHER_CLUSTER,
            authEndpoint: `${env.NEXT_PUBLIC_FLUID_API_URL}/public/stable/pusher/auth`,
          });

          pusherClient.onStateChange(handleStateChange);

          setConnectionStatus({
            state: "connected",
            isConnected: true,
          });
        } catch (error) {
          console.error(
            "[RealtimeProvider] Pusher initialization error:",
            error,
          );
          setConnectionStatus({
            state: "disconnected",
            isConnected: false,
            error: error as Error,
          });
        }
      });
    } else {
      Promise.resolve().then(() => {
        setConnectionStatus({
          state: "unavailable",
          isConnected: false,
        });
      });
    }

    return () => {
      if (isPusherEnabled) {
        pusherClient.offStateChange(handleStateChange);
        pusherClient.disconnect();
      }
    };
  }, [isPusherEnabled]);

  return (
    <RealtimeContext.Provider
      value={{
        connectionStatus,
        isPusherEnabled,
        isActionCableEnabled,
        actionCableUrl,
        actionCableConnectionStatus,
        setActionCableConnectionStatus,
      }}
    >
      {children}
    </RealtimeContext.Provider>
  );
}
